/**
 * The route table.
 *
 * Split out of `main.ts` so it can be imported without the side effects of the
 * entry point — `main.ts` pulls in the stylesheet and starts the router against
 * a real `document`, neither of which exists under vitest's node environment.
 * `routes.test.ts` registers this table and asks the router to resolve each
 * path, which is the check that a legal page a footer links to actually exists.
 *
 * Code splitting. Only the hub `/` and the round viewer's resolver
 * (`round-route.ts`, which serves the legacy `/round/:id` redirect and the
 * edition page's board layer) are in the entry chunk; the heavy live page is
 * split inside `round-route.ts` and fetched in parallel with its calendar read. Every other page is a `lazy` view: its chunk — players'
 * rating charts, the game and edition archives, search, the legal documents —
 * is downloaded on first visit, and the router keeps the current page up until
 * it arrives.
 */

import { homePage } from "./pages/home.js";
import { editionWatchPage, roundRoutePage } from "./pages/round-route.js";
import { lazy, route, type View } from "./router.js";
import { banner, masthead } from "./ui/chrome.js";
import { h } from "./ui/dom.js";

/** What a lazy page renders when its chunk cannot be fetched. */
const chunkFailed: View = (_route, mount) => {
  mount.append(h("div", { class: "shell" }, masthead(), banner("This page could not be loaded. Reload to try again.")));
};

export function registerRoutes(): void {
  route("/", homePage);
  // `/round/:id` is retired (O5) but stays registered for good: it redirects to
  // the round's tournament page, and when no edition resolves it renders the
  // viewer — the live plane, or the archive once published (#727).
  route("/round/:id", roundRoutePage);
  // The event's one page, the WATCH page (owner, 2026-09-23): a round in the
  // center, the whole event in the rail (`round-route.ts` `editionWatchPage`). Old
  // `/round/:id` links resolve their edition and redirect here.
  route("/edition/:id", editionWatchPage);
  route("/game/:id", lazy(() => import("./pages/game.js").then((m) => m.gamePage), chunkFailed));
  route("/search", lazy(() => import("./pages/search.js").then((m) => m.searchPage), chunkFailed));
  route("/players", lazy(() => import("./pages/players.js").then((m) => m.playersPage), chunkFailed));
  route("/players/leaderboard", lazy(() => import("./pages/leaderboard.js").then((m) => m.leaderboardPage), chunkFailed));
  route("/about", lazy(() => import("./pages/about.js").then((m) => m.aboutPage), chunkFailed));
  route("/player/:id", lazy(() => import("./pages/players.js").then((m) => m.playerPage), chunkFailed));
  route("/credits", lazy(() => import("./pages/credits.js").then((m) => m.creditsPage), chunkFailed));
  route("/privacy", lazy(() => import("./pages/legal.js").then((m) => m.privacyPage), chunkFailed));
  route("/terms", lazy(() => import("./pages/legal.js").then((m) => m.termsPage), chunkFailed));
  // The e-mail-change cancel link's landing page (#3549). The address is a published
  // contract: the function's CSL_EMAIL_CHANGE_CANCEL_URL names it, and mail already
  // sent keeps pointing here — `EMAIL_CHANGE_CANCEL_PATH`, held equal by its test.
  route("/account/email-change/cancel", lazy(() => import("./pages/email-change-cancel.js").then((m) => m.emailChangeCancelPage), chunkFailed));
}
